import type { ReactElement, ReactNode } from 'react';
import { ChevronRightIcon, PlayIcon } from '@heroicons/react/24/outline';
import { Link } from '@/components/ui/link';
import { Numeric } from '@/components/ui/numeric';
import { cn } from '@/lib/utils';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import { MatchStrip } from './MatchStrip';

export type MatchResultState = 'live' | 'upcoming' | 'finished' | 'winnerless';

export interface MatchResultSide {
  /** The caller's name node (link or text, plus a "You" badge). */
  name: ReactNode;
  roster?: string;
  /** The caller's rating/delta group, rendered inline after the name. */
  rating?: ReactNode;
  won: boolean;
  lost: boolean;
  /** `null` paints "–". */
  gamesWon: number | null;
  /** Empty = no per-game column. */
  games: { score: number; won: boolean }[];
  /** Screen-reader line for this side ("Games won: 2. Game scores: 11, 7, 11"). When set, the visual total and scores are `aria-hidden`. */
  scoresLabel?: string;
}

export type MatchResultAction =
  | { kind: 'scorecard'; href: string; label: string }
  | { kind: 'play'; onClick: () => void; label: string; active: boolean };

export interface MatchResultCardProps {
  /** `data-match-row`. */
  id: string;
  /** `data-match-status`; a live card inks its totals. */
  state: MatchResultState;
  /** Screen-reader status, always rendered. */
  statusWord: string;
  /** Screen-reader marker on the winning side. */
  winnerWord: string;
  header: {
    /** The match ID (A1, QUAL-B2), already formatted by `displayMatchCode`. */
    code?: string;
    court?: string;
    division?: string;
    status?: { kind: 'live' | 'upcoming' | 'outcome' | 'retired'; label: string };
    time?: ReactNode;
  };
  sides: [MatchResultSide, MatchResultSide];
  action?: MatchResultAction;
}

/** A name link's tap band: 44px tall below `md`, confined to the link, above the stretched card link. */
export const MATCH_CARD_NAME_LINK = 'relative z-[2] inline-block py-3 -my-3 md:py-2 md:-my-2';

function totalClass(state: MatchResultState, side: MatchResultSide): string {
  if (side.gamesWon === null) return 'bg-surface text-border-strong';
  if (state === 'finished' && side.won) return 'bg-primary text-primary-foreground';
  if (state === 'live') return 'bg-surface text-foreground';
  return 'bg-surface text-muted';
}

/**
 * The match card for lists: a header strip (match ID, court, division, status), two player rows with games
 * total and per-game scores, and one tap target over the whole card (scorecard link or play
 * button). Presentational: callers map their rows to this view-model.
 */
export function MatchResultCard({
  id,
  state,
  statusWord,
  winnerWord,
  header,
  sides,
  action,
}: MatchResultCardProps): ReactElement {
  const wide = sides.some((s) => s.games.some((g) => g.score >= 100));

  return (
    <li
      data-match-row={id}
      className={cn(
        'bg-card border-border-strong relative overflow-hidden rounded-lg border shadow-sm',
        action?.kind === 'scorecard' &&
          'hover:border-muted has-[a:focus-visible]:ring-ring has-[a:focus-visible]:ring-2',
        action?.kind === 'play' &&
          'hover:border-muted has-[button:focus-visible]:ring-ring ring-offset-background has-[button:focus-visible]:ring-2 has-[button:focus-visible]:ring-offset-2',
        action?.kind === 'play' && action.active && 'ring-primary ring-2',
      )}
    >
      <MatchStrip
        code={header.code}
        leading={header.time}
        court={header.court}
        division={header.division}
        status={header.status}
        statusWord={statusWord}
        state={state}
        trailing={
          <>
            {action?.kind === 'scorecard' && (
              <Link
                href={action.href}
                target="_blank"
                rel="noopener"
                aria-label={action.label}
                className="text-muted after:absolute after:inset-0 after:z-[1] after:content-[''] focus-visible:outline-none"
              >
                <ChevronRightIcon className="text-muted h-4 w-4" aria-hidden="true" />
              </Link>
            )}
            {action?.kind === 'play' && (
              <PlayIcon className="text-muted h-4 w-4" aria-hidden="true" />
            )}
          </>
        }
      />
      {sides.map((side, i) => (
        <div
          key={i}
          data-side
          className={cn(
            'relative flex min-h-11 flex-wrap items-center gap-x-2.5 gap-y-1 px-3 py-1 text-sm md:min-h-[38px]',
            i === 1 && 'border-border border-t',
            side.won &&
              'before:bg-bracket-path before:absolute before:inset-y-0 before:left-0 before:w-[3px]',
          )}
        >
          <div data-side-name className="flex min-w-0 flex-auto flex-wrap items-baseline gap-x-2">
            <span
              className={cn(
                'min-w-0',
                NAME_WRAP,
                side.won && 'font-bold',
                side.lost && 'text-muted font-normal',
              )}
            >
              {side.name}
            </span>
            {side.won && <span className="sr-only">{winnerWord}</span>}
            {side.rating}
            {side.roster && (
              <span className={cn('text-muted basis-full text-[0.6875rem]', NAME_WRAP)}>
                {side.roster}
              </span>
            )}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2.5">
            <Numeric
              data-side-games
              aria-hidden={side.scoresLabel ? 'true' : undefined}
              className={cn(
                'size-5 shrink-0 rounded-[5px] text-center text-[0.71875rem] leading-5 font-bold',
                totalClass(state, side),
              )}
            >
              {side.gamesWon ?? '–'}
            </Numeric>
            {side.scoresLabel && (
              <span data-side-scores-label className="sr-only">
                {side.scoresLabel}
              </span>
            )}
            {side.games.length > 0 && (
              <span
                data-side-scores
                aria-hidden={side.scoresLabel ? 'true' : undefined}
                className="flex shrink-0"
              >
                {side.games.map((game, k) => (
                  <Numeric
                    key={k}
                    className={cn(
                      'text-center text-xs leading-5',
                      wide ? 'w-6' : 'w-5',
                      game.won ? 'text-foreground font-bold' : 'text-muted',
                    )}
                  >
                    {game.score}
                  </Numeric>
                ))}
              </span>
            )}
          </div>
        </div>
      ))}
      {action?.kind === 'play' && (
        <button
          type="button"
          aria-label={action.label}
          aria-current={action.active ? 'true' : undefined}
          suppressHydrationWarning
          onClick={action.onClick}
          className="absolute inset-0 z-[1] cursor-pointer rounded-lg focus-visible:outline-none"
        />
      )}
    </li>
  );
}
